import { addTextConnectWidget } from '@frontend/assets';
import appConfig from '@frontend/env';
import { Trans, useTranslation } from '@frontend/i18n';
import { Text, TextLink } from '@frontend/design-system';
import { CopyHelper } from '../CopyHelper';
import * as styles from './styles';

interface Props {
  locationId: string;
  showInstructionLink?: boolean;
}

export const AddWidgetCard = ({ locationId, showInstructionLink = true }: Props) => {
  const { t } = useTranslation('schedule', { keyPrefix: 'text-connect' });

  return (
    <>
      <Text>
        {t(
          'When a customer visits your website a chat widget will appear in the bottom right corner of your homepage. Once a customer has clicked on that widget they will be able to send a message directly to your office.'
        )}
      </Text>

      <img
        alt={t('Text connect widget on example website.')}
        src={addTextConnectWidget}
        css={styles.addTextConnectWidgetImg}
      />

      <Text as='h3' weight='bold' css={styles.howToSetUp}>
        {t('How To Set Up')}
      </Text>
      {showInstructionLink && (
        <>
          <Text as='h4' weight='bold' css={styles.textConnectTextSpacing}>
            {t('Use your IT technician or website administrator')}
          </Text>

          <Text css={styles.textConnectTextSpacing}>
            {t(
              'Copy the URL below and send it to your website administrator. This will link to an instruction page they can use to set up the widget.'
            )}
          </Text>

          <CopyHelper helperText='' copyText={`${appConfig.APP_URL}/portal/text-connect-setup/${locationId}`} />

          <hr css={styles.divider} />

          <Text as='h4' weight='bold' css={styles.textConnectTextSpacing}>
            {t('Do it yourself')}
          </Text>
        </>
      )}
      <Trans t={t}>
        <Text>
          Add the widget to your website by inserting the script below into your website at the bottom of the{' '}
          <Text as='span' css={styles.codeText}>
            {'<head/> '}
          </Text>
          tag on the page of your choosing.
        </Text>
      </Trans>

      <CopyHelper
        helperText=''
        copyText={`<script defer src="${appConfig.APP_BOOK_BASE_URL}/text-connect/${locationId}/widget.js"></script>`}
      />

      <Text>{t('See articles for reference guides:')}</Text>

      <div css={styles.hostLinks}>
        <TextLink to='https://support.squarespace.com/hc/en-us/articles/216107348-Third-party-customizations'>
          {t('Squarespace')}
        </TextLink>

        <Text size='large' css={styles.dash}>
          -
        </Text>

        <TextLink to='https://www.wpbeginner.com/plugins/how-to-easily-add-custom-code-in-wordpress-without-breaking-your-site'>
          {t('Wordpress')}
        </TextLink>

        <Text size='large' css={styles.dash}>
          -
        </Text>

        <TextLink to='https://support.wix.com/en/article/embedding-custom-code-to-your-site'>{t('Wix')}</TextLink>

        <Text size='large' css={styles.dash}>
          -
        </Text>

        <TextLink to='https://www.weebly.com/app/help/us/en/topics/create-widgets-embed-code-and-add-external-content'>
          {t('Weebly')}
        </TextLink>
      </div>
    </>
  );
};
